/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { isMobile } from 'react-device-detect';
import { colorsForActions, colorsForTag, UserType } from '../notificationConstants';
import { TimeAndBadgeComponent } from './common';
import createMentionPlugin from '@draft-js-plugins/mention';
import lodash from 'lodash';
import { EditorState, convertFromRaw, CompositeDecorator } from 'draft-js';
import Editor from '@draft-js-plugins/editor';

const useStyle = createUseStyles({
    notificationDescription: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        marginTop: '4px',
    },
    headerTextStyle: {
        color: '#111',
        fontSize: '12px',
        fontWeight: 600,
    },
    notificationBody: {
        display: 'flex',
        flexDirection: 'row',
        gap: '5px',
        justifyContent: 'space-between',
    },
    messageText: {
        fontStyle: 'italic',
        fontSize: '10px',
    },
    descriptionText: {
        color: '#666',
        fontSize: '11px',
        fontWeight: 400,
        lineHeight: '1.4',
    },
});

const createStatus = (name: string): string => {
    const spacedWords = lodash.words(name);
    const capitalizedWords = lodash.map(spacedWords, (word) => lodash.upperFirst(word));
    return capitalizedWords.join(' ');
};

interface NotificationDescriptionProps {
    item: any;
    title?: string;
    notificationId: string;
    notificationStatus: Record<string, boolean>;
    setNotificationStatus: (status: Record<string, boolean>) => void;
    sourceName: string;
    userId?: string | null;
    organisationId?: string | null;
    userType?: string;
    entityDescription?: string;
}

export const NotificationDescription = (props: NotificationDescriptionProps) => {
    const classes = useStyle();
    const {
        item,
        title = '',
        notificationId,
        notificationStatus,
        setNotificationStatus,
        sourceName,
        userId,
        organisationId,
        userType,
        entityDescription,
    } = props;

    const { plugins } = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            entityMutability: 'IMMUTABLE',
            mentionPrefix: '@',
            supportWhitespace: true,
        });

        const pluginsList = [mentionPlugin];
        return { plugins: pluginsList };
    }, []);

    const decorators = lodash.flattenDeep(
        plugins.map((plugin: any) => plugin.decorators),
    );
    const decorator = new CompositeDecorator(
        decorators.filter((_decorator: any, index: number) => index !== 1),
    );

    // For worker userType, display entityDescription directly
    if (userType === UserType.WORKER) {
        return (
            <div className={classes.notificationDescription}>
                {entityDescription && (
                    <div className={classes.descriptionText}>{entityDescription}</div>
                )}
                {isMobile && (
                    <div className={classes.notificationBody}>
                        <div />
                        <TimeAndBadgeComponent
                            item={item}
                            notificationId={notificationId}
                            notificationStatus={notificationStatus}
                            setNotificationStatus={setNotificationStatus}
                            userId={userId}
                            organisationId={organisationId}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={classes.notificationDescription}>
            {title && <div className={classes.headerTextStyle}>{title}</div>}
            {(isMobile
        || item.extraData?.comment
        || item.extraData?.previousStatus
        || item.extraData?.currentAssignee)
        && (
            <div className={classes.notificationBody}>
                <div>
                    {item.extraData?.comment && (
                        <div className={classes.messageText}>
                            <Editor
                                readOnly
                                plugins={plugins}
                                editorState={EditorState.createWithContent(
                                    convertFromRaw(item.extraData?.comment),
                                    decorator,
                                )}
                                onChange={() => {}}
                            />
                        </div>
                    )}
                    {item.extraData?.previousStatus
              && item.extraData?.currentStatus
              && (
                  <div style={{ marginTop: '4px' }}>
                      <Tag color={colorsForActions[item.extraData?.previousStatus]}>
                          {createStatus(item.extraData?.previousStatus)}
                      </Tag>
                      <ArrowRightOutlined />
                      {' '}
                      <Tag color={colorsForActions[item.extraData?.currentStatus]}>
                          {createStatus(item.extraData?.currentStatus)}
                      </Tag>
                  </div>
              )}
                    {item.extraData?.currentAssignee && (
                        <div style={{ marginTop: '4px' }}>
                            {sourceName === 'support_ticket_notification' ? (
                                <Tag
                                    color={
                                        colorsForTag[
                                            item.extraData?.currentAssignee.length ?? 0 % 11
                                        ]
                                    }
                                >
                                    {item.extraData?.currentAssignee}
                                </Tag>
                            ) : item.extraData?.previousAssignee && item.extraData?.currentAssignee ? (
                                <>
                                    <Tag
                                        color={
                                            colorsForTag[
                                                item.extraData?.previousAssignee.length ?? 0 % 11
                                            ]
                                        }
                                    >
                                        {item.extraData?.previousAssignee}
                                    </Tag>
                                    <ArrowRightOutlined />
                                    {' '}
                                    <Tag
                                        color={
                                            colorsForTag[
                                                item.extraData?.currentAssignee.length ?? 0 % 11
                                            ]
                                        }
                                    >
                                        {item.extraData?.currentAssignee}
                                    </Tag>
                                </>
                            ) : null}
                        </div>
                    )}
                </div>
                {isMobile && (
                    <TimeAndBadgeComponent
                        item={item}
                        notificationId={notificationId}
                        notificationStatus={notificationStatus}
                        setNotificationStatus={setNotificationStatus}
                        userId={userId}
                        organisationId={organisationId}
                    />
                )}
            </div>
        )}
        </div>
    );
};
